import { useState, useEffect, useMemo, useCallback } from 'react';

// Fiyatlar. Veri saatte bir Ozon'dan DB'ye çekilir (syncPrices.js).
// Satıcı fiyatları USD (currency_code), müşteri fiyatı RUB (Ozon Kart indirimi hariç).
// Fiyat değiştirme: sadece admin; önce önizleme, sonra onayla Ozon'a gönderim (priceWriter.js).

const BASE = '/api';
function api(method, path, body) {
  const token = localStorage.getItem('ozon_token');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || r.statusText);
    return d;
  });
}

// Ozon tarihleri UTC gelir; ekranda Moskova saatiyle gösterilir.
function formatDate(str, saatli = false) {
  if (!str) return '—';
  return new Date(str).toLocaleString('tr-TR', {
    timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric',
    ...(saatli ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

function para(v, kod) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (kod === 'RUB') return `${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₽`;
  const s = n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return kod === 'USD' || !kod ? `$${s}` : `${s} ${kod}`;
}

const ENDEKS_RENK = {
  GREEN:  'bg-green-100 text-green-700',
  YELLOW: 'bg-yellow-100 text-yellow-700',
  RED:    'bg-red-100 text-red-700',
};
const ENDEKS_AD = { GREEN: 'İyi', YELLOW: 'Orta', RED: 'Kötü', WITHOUT_INDEX: 'Yok' };

const FILTRELER = [
  { key: 'tumu',    label: 'Tümü' },
  { key: 'katilan', label: 'Ozon kampanyasında' },
  { key: 'aday',    label: 'Ozon kampanyasına girebilir' },
  { key: 'urun',    label: 'Ürün kampanyası var' },
  { key: 'yok',     label: 'Kampanyasız' },
];

const DURUM = {
  gonderildi: { ad: 'Gönderildi', renk: 'bg-green-100 text-green-700' },
  reddedildi: { ad: 'Ozon reddetti', renk: 'bg-red-100 text-red-700' },
  atlandi:    { ad: 'Atlandı', renk: 'bg-yellow-100 text-yellow-700' },
  hata:       { ad: 'Hata', renk: 'bg-red-100 text-red-700' },
};

function katildigi(u)  { return u.ozon_kampanyalari.filter(k => k.durum === 'katilan'); }
function adayOldugu(u) { return u.ozon_kampanyalari.filter(k => k.durum === 'aday'); }

function filtreUygun(u, f) {
  const kat = katildigi(u).length, aday = adayOldugu(u).length, urun = u.urun_kampanyalari.length;
  if (f === 'katilan') return kat > 0;
  if (f === 'aday')    return aday > 0;
  if (f === 'urun')    return urun > 0;
  if (f === 'yok')     return kat === 0 && urun === 0;
  return true;
}

function KampanyaListesi({ kampanyalar }) {
  if (!kampanyalar.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-4 overflow-x-auto">
      <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">
        Ozon kampanyaları
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-400 text-left">
            <th className="px-4 py-2 font-medium">Kampanya</th>
            <th className="px-4 py-2 font-medium">Tarih</th>
            <th className="px-4 py-2 font-medium text-right">Katılan</th>
            <th className="px-4 py-2 font-medium text-right">Girebilecek</th>
          </tr>
        </thead>
        <tbody>
          {kampanyalar.map(k => (
            <tr key={k.id} className="border-t border-gray-50">
              <td className="px-4 py-2">
                <span className="text-gray-800">{k.title}</span>
                {k.is_participating && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Katılıyorsun</span>
                )}
                <div className="text-xs text-gray-400">{k.action_type}</div>
              </td>
              <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                {formatDate(k.date_start)} – {formatDate(k.date_end)}
              </td>
              <td className="px-4 py-2 text-right">{k.participating_products_count ?? '—'}</td>
              <td className="px-4 py-2 text-right text-gray-500">{k.potential_products_count ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function bitisGunu(tarih) {
  if (!tarih) return null;
  return Math.ceil((new Date(tarih) - Date.now()) / 86400000);
}

function SaticiKampanyaListesi({ kampanyalar }) {
  if (!kampanyalar.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-4 overflow-x-auto">
      <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">
        Kendi kampanyaların
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-400 text-left">
            <th className="px-4 py-2 font-medium">Kampanya</th>
            <th className="px-4 py-2 font-medium">Tarih</th>
            <th className="px-4 py-2 font-medium text-right">Ürün</th>
            <th className="px-4 py-2 font-medium">Durum</th>
          </tr>
        </thead>
        <tbody>
          {kampanyalar.map(k => {
            const gun = bitisGunu(k.date_end);
            return (
              <tr key={k.action_id} className="border-t border-gray-50">
                <td className="px-4 py-2">
                  <span className="text-gray-800">{k.title}</span>
                  <div className="text-xs text-gray-400">
                    {k.discount_type === 'FINAL_PRICE' ? 'Ürün başına sabit fiyat' : `${k.type} · ${k.discount_type}`}
                    {Number(k.min_action_percent) > 0 && ` · en az %${Number(k.min_action_percent)} indirim`}
                  </div>
                </td>
                <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                  {formatDate(k.date_start)} – {formatDate(k.date_end)}
                  {gun !== null && gun <= 10 && (
                    <div className={`text-xs ${gun <= 3 ? 'text-red-600 font-semibold' : 'text-amber-600'}`}>
                      {gun <= 0 ? 'Bugün bitiyor' : `${gun} gün sonra bitiyor`}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-right">{k.sku_count ?? '—'}</td>
                <td className="px-4 py-2 text-xs text-gray-600">{k.status}{k.is_turn_on === false ? ' · kapalı' : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UrunSatiri({ u, admin, secili, onSec, onDuzenle, onKampanyaDuzenle }) {
  const kat = katildigi(u), aday = adayOldugu(u);
  const kod = u.currency_code;
  const saticiAdlari = new Set(u.satici_kampanyalari.map(k => k.title));
  // Satıcı kampanyası /v5 listesinde de görünür; aynı rozeti iki kez göstermeyelim.
  const digerKampanyalar = u.urun_kampanyalari.filter(a => !saticiAdlari.has(a.title));
  // "Kampanya sonrası" fiyatın yanındaki ✎: müşterinin ödediği fiyatı belirleyen kendi kampanyan.
  const duzenlenirKampanya = admin
    ? u.satici_kampanyalari.find(k => k.discount_type === 'FINAL_PRICE' && k.is_editable !== false)
    : null;
  return (
    <tr className={`border-t border-gray-50 align-top ${secili ? 'bg-blue-50/40' : ''}`}>
      {admin && (
        <td className="pl-4 py-3">
          <input type="checkbox" checked={secili} onChange={() => onSec(u.offer_id)} />
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex gap-3">
          {u.image_url
            ? <img src={u.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
            : <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0" />}
          <div className="min-w-0">
            <div className="font-mono text-xs text-gray-800">{u.offer_id}</div>
            <div className="text-xs text-gray-400 truncate max-w-xs">{u.name || '—'}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <div className="font-semibold text-gray-900">
          {para(u.price, kod)}
          {admin && (
            <button onClick={() => onDuzenle(u)} title="Fiyatı düzenle"
              className="ml-2 text-xs text-blue-500 hover:text-blue-700">✎</button>
          )}
        </div>
        {Number(u.old_price) > 0 && <div className="text-xs text-gray-400 line-through">{para(u.old_price, kod)}</div>}
        {Number(u.min_price) > 0 && <div className="text-xs text-gray-400">min {para(u.min_price, kod)}</div>}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {para(u.marketing_seller_price, kod)}
        {duzenlenirKampanya && (
          <button onClick={() => onKampanyaDuzenle(u, duzenlenirKampanya)}
            title={`"${duzenlenirKampanya.title}" kampanya fiyatını düzenle`}
            className="ml-2 text-xs text-green-600 hover:text-green-800">✎</button>
        )}
        {duzenlenirKampanya && <div className="text-xs text-green-700">{duzenlenirKampanya.title}</div>}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <div className="font-semibold text-gray-900">{para(u.customer_price_rub, 'RUB')}</div>
        {u.price_rub && Number(u.price_rub) !== Number(u.customer_price_rub) && (
          <div className="text-xs text-gray-400 line-through">{para(u.price_rub, 'RUB')}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {kat.map(k => (
            <span key={`k${k.action_id}`} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700"
              title={`Kampanya fiyatı ${para(k.action_price, kod)} · tavan ${para(k.max_action_price, kod)} · bitiş ${formatDate(k.date_end)}`}>
              {k.title} · {para(k.action_price, kod)}
            </span>
          ))}
          {u.satici_kampanyalari.map(k => {
            const duzenlenir = admin && k.discount_type === 'FINAL_PRICE' && k.is_editable !== false;
            return (
              <span key={`s${k.action_id}`}
                onClick={duzenlenir ? () => onKampanyaDuzenle(u, k) : undefined}
                className={`text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 ${duzenlenir ? 'cursor-pointer hover:bg-green-200' : ''}`}
                title={`Kendi kampanyan · bitiş ${formatDate(k.date_end)}${duzenlenir ? ' · düzenlemek için tıkla' : ''}`}>
                {k.title} · {para(k.action_price, kod)}{duzenlenir && ' ✎'}
              </span>
            );
          })}
          {digerKampanyalar.map((a, i) => (
            <span key={`u${i}`} className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700"
              title={`${formatDate(a.date_from)} – ${formatDate(a.date_to)}`}>
              {a.title}
            </span>
          ))}
          {aday.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500"
              title={aday.map(k => `${k.title} (tavan ${para(k.max_action_price, kod)})`).join('\n')}>
              +{aday.length} kampanyaya girebilir
            </span>
          )}
          {!kat.length && !u.urun_kampanyalari.length && !u.satici_kampanyalari.length && !aday.length && (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {u.color_index && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${ENDEKS_RENK[u.color_index] || 'bg-gray-100 text-gray-500'}`}>
            {ENDEKS_AD[u.color_index] || u.color_index}
          </span>
        )}
      </td>
    </tr>
  );
}

function Kutu({ children, onClose, genis }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${genis ? 'max-w-5xl' : 'max-w-sm'} p-6 max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function DuzenleKutusu({ u, onKapat, onOnizle }) {
  const [deger, setDeger] = useState({
    price: String(Number(u.price ?? 0)),
    old_price: String(Number(u.old_price ?? 0)),
    min_price: String(Number(u.min_price ?? 0)),
  });
  const alan = (key, label, aciklama) => (
    <div className="mb-3">
      <label className="text-xs font-semibold text-gray-500 block mb-1">{label} ({u.currency_code})</label>
      <input type="number" step="0.01" min="0" value={deger[key]}
        onChange={e => setDeger(d => ({ ...d, [key]: e.target.value }))}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
      {aciklama && <p className="text-xs text-gray-400 mt-1">{aciklama}</p>}
    </div>
  );
  return (
    <Kutu onClose={onKapat}>
      <h2 className="font-bold text-gray-900 mb-1">Fiyatı düzenle</h2>
      <p className="text-xs text-gray-500 mb-4 font-mono">{u.offer_id}</p>
      {alan('price', 'Fiyat')}
      {alan('old_price', 'Üstü çizili fiyat', 'İndirim göstermek istemiyorsan 0')}
      {alan('min_price', 'En düşük fiyat', 'Ozon otomatik indirimlerde bunun altına inmez')}
      <div className="flex gap-2 mt-4">
        <button onClick={onKapat} className="flex-1 text-sm py-2 rounded-xl border border-gray-200 text-gray-600">Vazgeç</button>
        <button onClick={() => onOnizle([{ offer_id: u.offer_id, ...deger }])}
          className="flex-1 text-sm py-2 rounded-xl bg-gray-900 text-white">Önizle</button>
      </div>
    </Kutu>
  );
}

function KampanyaDuzenleKutusu({ u, k, onKapat, onOnizle }) {
  const [deger, setDeger] = useState(String(Number(k.action_price ?? 0)));
  const minYuzde = Number(k.min_action_percent) || 0;
  const tavan = Math.round(Number(u.price) * (1 - minYuzde / 100) * 100) / 100;
  return (
    <Kutu onClose={onKapat}>
      <h2 className="font-bold text-gray-900 mb-1">Kampanya fiyatını düzenle</h2>
      <p className="text-xs text-gray-500 mb-1 font-mono">{u.offer_id}</p>
      <p className="text-xs text-green-700 font-semibold mb-4">{k.title} · bitiş {formatDate(k.date_end)}</p>
      <label className="text-xs font-semibold text-gray-500 block mb-1">Kampanya fiyatı ({u.currency_code})</label>
      <input type="number" step="0.01" min="0" value={deger} onChange={e => setDeger(e.target.value)}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
      <div className="text-xs text-gray-400 mt-2 space-y-0.5">
        <div>Müşteri bu fiyatı öder. Ürünün kendi fiyatı: {para(u.price, u.currency_code)}</div>
        <div>En çok {para(tavan, u.currency_code)} olabilir (ürün fiyatından en az %{minYuzde} düşük)</div>
        {Number(u.min_price) > 0 && <div>En az {para(u.min_price, u.currency_code)} (en düşük fiyatın)</div>}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={onKapat} className="flex-1 text-sm py-2 rounded-xl border border-gray-200 text-gray-600">Vazgeç</button>
        <button onClick={() => onOnizle([{ action_id: k.action_id, offer_id: u.offer_id, action_price: deger }], 'kampanya')}
          className="flex-1 text-sm py-2 rounded-xl bg-gray-900 text-white">Önizle</button>
      </div>
    </Kutu>
  );
}

function OnizlemeKutusu({ onizleme, yazmaAcik, onKapat, onGonderildi }) {
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const [hata, setHata] = useState('');
  const { satirlar, degisiklikler, tur, not } = onizleme;
  const kampanya = tur === 'kampanya';
  const anahtar = s => (kampanya ? `${s.action_id}|${s.offer_id}` : s.offer_id);
  const gecerli = satirlar.filter(s => !s.hatalar.length);
  const uyarili = gecerli.filter(s => s.uyarilar.length).length;

  async function gonder() {
    const mesaj = `${gecerli.length} ürünün ${kampanya ? 'KAMPANYA fiyatı' : 'fiyatı'} Ozon'da değiştirilecek`
      + (uyarili ? ` (${uyarili} üründe uyarı var)` : '') + '. Emin misin?';
    if (!window.confirm(mesaj)) return;
    setGonderiliyor(true); setHata('');
    try {
      const gecerliIdler = new Set(gecerli.map(anahtar));
      const r = await api('POST', kampanya ? '/prices/kampanya/uygula' : '/prices/uygula',
        { degisiklikler: degisiklikler.filter(d => gecerliIdler.has(anahtar(d))) });
      setSonuc(r.sonuc);
      onGonderildi();
    } catch (e) { setHata(e.message); }
    finally { setGonderiliyor(false); }
  }

  const ok = (eski, yeni, kod) => ((Number(eski) || 0) === (Number(yeni) || 0)
    ? <span className="text-gray-400">{para(yeni, kod)}</span>
    : <><span className="text-gray-400 line-through">{para(eski, kod)}</span> → <b>{para(yeni, kod)}</b></>);

  if (sonuc) {
    return (
      <Kutu onClose={onKapat} genis>
        <h2 className="font-bold text-gray-900 mb-3">Sonuç</h2>
        <table className="w-full text-sm mb-4">
          <thead><tr className="text-xs text-gray-400 text-left">
            <th className="py-1 font-medium">Ürün</th><th className="py-1 font-medium">Durum</th>
            <th className="py-1 font-medium text-right">{kampanya ? 'Yeni kampanya fiyatı' : 'Yeni fiyat'}</th>
            <th className="py-1 font-medium text-right">Ozon'da okunan</th>
            <th className="py-1 font-medium">Not</th>
          </tr></thead>
          <tbody>
            {sonuc.map((s, i) => (
              <tr key={i} className="border-t border-gray-50 align-top">
                <td className="py-2 font-mono text-xs">{s.offer_id}{kampanya && s.kampanya && <div className="text-gray-400 font-sans">{s.kampanya}</div>}</td>
                <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${DURUM[s.durum]?.renk}`}>{DURUM[s.durum]?.ad || s.durum}</span></td>
                <td className="py-2 text-right">{s.yeni ? para(kampanya ? s.yeni.action_price : s.yeni.price) : '—'}</td>
                <td className="py-2 text-right">{para(kampanya ? s.dogrulanan_action_price : s.dogrulanan_price)}</td>
                <td className="py-2 text-xs text-gray-500">{s.mesaj || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={onKapat} className="w-full text-sm py-2 rounded-xl bg-gray-900 text-white">Kapat</button>
      </Kutu>
    );
  }

  return (
    <Kutu onClose={onKapat} genis>
      <h2 className="font-bold text-gray-900 mb-1">Önizleme{kampanya ? ' — kampanya fiyatı' : ''}</h2>
      <p className="text-xs text-gray-500 mb-3">
        Henüz hiçbir şey gönderilmedi. {gecerli.length} ürün gönderilebilir
        {satirlar.length - gecerli.length > 0 && `, ${satirlar.length - gecerli.length} ürün hatalı (gönderilmeyecek)`}.
        {not && <> {not}</>}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm mb-4">
          <thead><tr className="text-xs text-gray-400 text-left">
            <th className="py-1 font-medium">Ürün</th>
            {kampanya ? <>
              <th className="py-1 font-medium text-right">Kampanya fiyatı</th>
              <th className="py-1 font-medium text-right">Ürün fiyatı</th>
            </> : <>
              <th className="py-1 font-medium text-right">Fiyat</th>
              <th className="py-1 font-medium text-right">Üstü çizili</th>
              <th className="py-1 font-medium text-right">En düşük</th>
            </>}
            <th className="py-1 font-medium pl-4">Kontrol</th>
          </tr></thead>
          <tbody>
            {satirlar.map(s => (
              <tr key={anahtar(s)} className={`border-t border-gray-50 align-top ${s.hatalar.length ? 'opacity-60' : ''}`}>
                <td className="py-2 font-mono text-xs">{s.offer_id}{kampanya && s.kampanya && <div className="text-green-700 font-sans">{s.kampanya}</div>}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  {s.yeni ? (kampanya ? ok(s.eski.action_price, s.yeni.action_price, s.currency_code) : ok(s.eski.price, s.yeni.price, s.currency_code)) : '—'}
                  {s.degisim_yuzde != null && (
                    <div className={`text-xs ${s.degisim_yuzde < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {s.degisim_yuzde > 0 ? '+' : ''}{s.degisim_yuzde}%
                    </div>
                  )}
                </td>
                {kampanya ? (
                  <td className="py-2 text-right whitespace-nowrap text-gray-500">{para(s.fiyat, s.currency_code)}</td>
                ) : <>
                  <td className="py-2 text-right whitespace-nowrap">{s.yeni ? ok(s.eski.old_price, s.yeni.old_price, s.currency_code) : '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">{s.yeni ? ok(s.eski.min_price, s.yeni.min_price, s.currency_code) : '—'}</td>
                </>}
                <td className="py-2 pl-4 text-xs">
                  {s.hatalar.map((h, i) => <div key={`h${i}`} className="text-red-600">✕ {h}</div>)}
                  {s.uyarilar.map((h, i) => <div key={`u${i}`} className="text-amber-600">⚠ {h}</div>)}
                  {!s.hatalar.length && !s.uyarilar.length && <span className="text-green-600">✓</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!yazmaAcik && (
        <p className="text-xs text-red-600 mb-3">Fiyat değiştirme kapalı. Göndermek için sayfanın üstündeki anahtarı aç.</p>
      )}
      {hata && <p className="text-xs text-red-600 mb-3">Hata: {hata}</p>}
      <div className="flex gap-2">
        <button onClick={onKapat} className="flex-1 text-sm py-2 rounded-xl border border-gray-200 text-gray-600">Vazgeç</button>
        <button onClick={gonder} disabled={!yazmaAcik || !gecerli.length || gonderiliyor}
          className="flex-1 text-sm py-2 rounded-xl bg-blue-600 text-white disabled:opacity-40">
          {gonderiliyor ? 'Gönderiliyor...' : `Ozon'a gönder (${gecerli.length} ürün)`}
        </button>
      </div>
    </Kutu>
  );
}

function Gecmis() {
  const [satirlar, setSatirlar] = useState(null);
  const [hata, setHata] = useState('');
  useEffect(() => { api('GET', '/prices/gecmis').then(setSatirlar).catch(e => setHata(e.message)); }, []);
  if (hata) return <p className="text-xs text-red-600 p-4">Hata: {hata}</p>;
  if (!satirlar) return <p className="text-xs text-gray-400 p-4">Yükleniyor...</p>;
  if (!satirlar.length) return <p className="text-xs text-gray-400 p-4">Henüz fiyat değişikliği yok.</p>;
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-xs text-gray-400 text-left">
        <th className="px-4 py-2 font-medium">Zaman</th><th className="px-4 py-2 font-medium">Ürün</th>
        <th className="px-4 py-2 font-medium text-right">Fiyat</th><th className="px-4 py-2 font-medium">Durum</th>
        <th className="px-4 py-2 font-medium">Kullanıcı</th><th className="px-4 py-2 font-medium">Not</th>
      </tr></thead>
      <tbody>
        {satirlar.map(s => (
          <tr key={s.id} className="border-t border-gray-50 align-top">
            <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">{formatDate(s.created_at, true)}</td>
            <td className="px-4 py-2 font-mono text-xs">{s.offer_id}</td>
            <td className="px-4 py-2 text-right whitespace-nowrap">
              {s.tur === 'kampanya' && <div className="text-xs text-green-700">kampanya fiyatı</div>}
              <span className="text-gray-400">{para(s.tur === 'kampanya' ? s.eski_action_price : s.eski_price, s.currency_code)}</span>
              {' → '}{para(s.tur === 'kampanya' ? s.yeni_action_price : s.yeni_price, s.currency_code)}
            </td>
            <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${DURUM[s.durum]?.renk}`}>{DURUM[s.durum]?.ad || s.durum}</span></td>
            <td className="px-4 py-2 text-xs text-gray-500">{s.kullanici || '—'}</td>
            <td className="px-4 py-2 text-xs text-gray-500">{s.mesaj || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function Prices() {
  const [veri,      setVeri]      = useState(null);
  const [hata,      setHata]      = useState('');
  const [arama,     setArama]     = useState('');
  const [filtre,    setFiltre]    = useState('tumu');
  const [secili,    setSecili]    = useState(() => new Set());
  const [yuzde,     setYuzde]     = useState('');
  const [ustuCizili, setUstuCizili] = useState(true);
  const [duzenlenen, setDuzenlenen] = useState(null);
  const [kampanyaDuzenlenen, setKampanyaDuzenlenen] = useState(null);   // { u, k }
  const [hedef,     setHedef]     = useState('fiyat');   // toplu %: 'fiyat' | satıcı kampanya action_id
  const [onizleme,  setOnizleme]  = useState(null);
  const [islemHata, setIslemHata] = useState('');
  const [gecmisAcik, setGecmisAcik] = useState(false);
  const [gecmisAnahtar, setGecmisAnahtar] = useState(0);

  const yukle = useCallback(() => {
    api('GET', '/prices').then(setVeri).catch(e => setHata(e.message));
  }, []);
  useEffect(() => { yukle(); }, [yukle]);

  const urunler = useMemo(() => {
    if (!veri) return [];
    const q = arama.trim().toLowerCase();
    return veri.urunler.filter(u =>
      filtreUygun(u, filtre) &&
      (!q || u.offer_id.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q)));
  }, [veri, arama, filtre]);

  const sayac = useMemo(() => {
    const s = {};
    if (veri) FILTRELER.forEach(f => { s[f.key] = veri.urunler.filter(u => filtreUygun(u, f.key)).length; });
    return s;
  }, [veri]);

  function sec(id) {
    setSecili(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function tumunuSec() {
    const hepsiSecili = urunler.length > 0 && urunler.every(u => secili.has(u.offer_id));
    setSecili(s => {
      const n = new Set(s);
      urunler.forEach(u => (hepsiSecili ? n.delete(u.offer_id) : n.add(u.offer_id)));
      return n;
    });
  }

  async function onizle(degisiklikler, tur = 'fiyat', not = '') {
    setIslemHata('');
    try {
      const r = await api('POST', tur === 'kampanya' ? '/prices/kampanya/onizle' : '/prices/onizle', { degisiklikler });
      setDuzenlenen(null);
      setKampanyaDuzenlenen(null);
      setOnizleme({ satirlar: r.satirlar, degisiklikler, tur, not });
    } catch (e) { setIslemHata(e.message); }
  }

  function topluOnizle() {
    const p = Number(String(yuzde).replace(',', '.'));
    if (!Number.isFinite(p) || p === 0) { setIslemHata('Geçerli bir yüzde gir (örn. 10 veya -5)'); return; }
    const carpan = 1 + p / 100;
    const seciliUrunler = veri.urunler.filter(u => secili.has(u.offer_id));
    if (hedef !== 'fiyat') {
      const actionId = Number(hedef);
      const kampanyada = seciliUrunler
        .map(u => ({ u, k: u.satici_kampanyalari.find(k => Number(k.action_id) === actionId) }))
        .filter(x => x.k);
      if (!kampanyada.length) { setIslemHata('Seçili ürünlerin hiçbiri bu kampanyada değil'); return; }
      const disarida = seciliUrunler.length - kampanyada.length;
      onizle(kampanyada.map(({ u, k }) => ({
        action_id: actionId, offer_id: u.offer_id,
        action_price: Math.round(Number(k.action_price) * carpan * 100) / 100,
      })), 'kampanya', disarida ? `${disarida} seçili ürün bu kampanyada olmadığı için listede yok.` : '');
      return;
    }
    const degisiklikler = seciliUrunler.map(u => ({
      offer_id: u.offer_id,
      price: Math.round(Number(u.price) * carpan * 100) / 100,
      ...(ustuCizili && Number(u.old_price) > 0
        ? { old_price: Math.round(Number(u.old_price) * carpan * 100) / 100 } : {}),
    }));
    onizle(degisiklikler);
  }

  async function yazmaDegistir() {
    const acilacak = !veri.yazma.acik;
    if (acilacak && !window.confirm('Fiyat değiştirme açılsın mı? Açıkken bu ekrandan Ozon\'daki fiyatlar değiştirilebilir.')) return;
    try {
      const r = await api('PUT', '/prices/yazma', { acik: acilacak });
      setVeri(v => ({ ...v, yazma: { ...v.yazma, acik: r.acik } }));
    } catch (e) { setIslemHata(e.message); }
  }

  if (hata) return <div className="p-6 text-red-600 text-sm">Hata: {hata}</div>;
  if (!veri) return <div className="p-6 text-gray-400 text-sm">Yükleniyor...</div>;

  const sk = veri.senkron;
  const admin = veri.yazma?.admin;
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Fiyatlar</h1>
          <p className="text-xs text-gray-400">
            Satıştaki ürünler · fiyatlar Ozon'dan saatte bir güncellenir · müşteri fiyatına Ozon Kart indirimi dahil değil
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {admin && (
            <button onClick={yazmaDegistir}
              className={`text-xs px-3 py-1.5 rounded-full border ${veri.yazma.acik
                ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-gray-500 border-gray-200'}`}>
              Fiyat değiştirme: <b>{veri.yazma.acik ? 'AÇIK' : 'Kapalı'}</b>
            </button>
          )}
          <div className="text-xs text-gray-500">
            {!sk && 'Henüz senkron çalışmadı'}
            {sk && sk.ok === true  && <>Son güncelleme: {formatDate(sk.finished_at, true)}</>}
            {sk && sk.ok === false && <span className="text-red-600">Son senkron hata verdi ({formatDate(sk.started_at, true)}): {sk.message}</span>}
            {sk && sk.ok === null  && <>Senkron sürüyor ({formatDate(sk.started_at, true)})</>}
          </div>
        </div>
      </div>

      <SaticiKampanyaListesi kampanyalar={veri.satici_kampanyalari || []} />
      <KampanyaListesi kampanyalar={veri.kampanyalar} />

      <div className="flex flex-wrap gap-2 mb-3">
        {FILTRELER.map(f => (
          <button key={f.key} onClick={() => setFiltre(f.key)}
            className={`text-xs px-3 py-1.5 rounded-full border ${filtre === f.key
              ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>
            {f.label} <span className="opacity-60">{sayac[f.key]}</span>
          </button>
        ))}
        <input value={arama} onChange={e => setArama(e.target.value)} placeholder="Ürün kodu veya ad ara"
          className="ml-auto text-sm border border-gray-200 rounded-xl px-3 py-1.5 w-full sm:w-64 focus:outline-none focus:border-blue-400" />
      </div>

      {admin && secili.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-2 text-sm">
          <span className="font-semibold text-blue-800">{secili.size} ürün seçili</span>
          <select value={hedef} onChange={e => setHedef(e.target.value)}
            className="border border-gray-200 rounded-xl px-2 py-1 text-sm bg-white focus:outline-none focus:border-blue-400">
            <option value="fiyat">Ürün fiyatını</option>
            {(veri.satici_kampanyalari || []).filter(k => k.discount_type === 'FINAL_PRICE' && k.is_editable !== false).map(k => (
              <option key={k.action_id} value={String(k.action_id)}>"{k.title}" kampanya fiyatını</option>
            ))}
          </select>
          <input value={yuzde} onChange={e => setYuzde(e.target.value)} placeholder="10"
            className="w-20 border border-gray-200 rounded-xl px-2 py-1 text-sm focus:outline-none focus:border-blue-400" />
          <span className="text-gray-500">% değiştir (düşürmek için eksi)</span>
          {hedef === 'fiyat' && (
            <label className="flex items-center gap-1 text-xs text-gray-600">
              <input type="checkbox" checked={ustuCizili} onChange={e => setUstuCizili(e.target.checked)} />
              Üstü çizili fiyatı da aynı oranda
            </label>
          )}
          <button onClick={topluOnizle} className="text-xs px-3 py-1.5 rounded-xl bg-gray-900 text-white">Önizle</button>
          <button onClick={() => setSecili(new Set())} className="text-xs text-gray-500 ml-auto">Seçimi temizle</button>
        </div>
      )}
      {islemHata && <p className="text-xs text-red-600 mb-3">Hata: {islemHata}</p>}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-400 text-left">
              {admin && (
                <th className="pl-4 py-2">
                  <input type="checkbox" onChange={tumunuSec}
                    checked={urunler.length > 0 && urunler.every(u => secili.has(u.offer_id))} />
                </th>
              )}
              <th className="px-4 py-2 font-medium">Ürün</th>
              <th className="px-4 py-2 font-medium text-right">Fiyatın</th>
              <th className="px-4 py-2 font-medium text-right">Kampanya sonrası</th>
              <th className="px-4 py-2 font-medium text-right">Müşteri görür</th>
              <th className="px-4 py-2 font-medium">Kampanyalar</th>
              <th className="px-4 py-2 font-medium">Fiyat endeksi</th>
            </tr>
          </thead>
          <tbody>
            {urunler.map(u => (
              <UrunSatiri key={u.offer_id} u={u} admin={admin} secili={secili.has(u.offer_id)}
                onSec={sec} onDuzenle={setDuzenlenen} onKampanyaDuzenle={(u, k) => setKampanyaDuzenlenen({ u, k })} />
            ))}
            {!urunler.length && (
              <tr><td colSpan={admin ? 7 : 6} className="px-4 py-8 text-center text-gray-400 text-sm">Ürün yok</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-2">
        Yeşil: kendi kampanyan (kampanya fiyatıyla; ✎ ile düzenlenir) · Mavi: Ozon kampanyası · Mor: ürüne bağlı diğer kampanyalar (taksit vb.) · Rozetin üstüne gelince ayrıntı görünür.
      </p>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mt-4">
        <button onClick={() => setGecmisAcik(a => !a)}
          className="w-full text-left px-4 py-3 text-sm font-semibold text-gray-700">
          {gecmisAcik ? '▾' : '▸'} Fiyat değişikliği geçmişi
        </button>
        {gecmisAcik && <div className="overflow-x-auto border-t border-gray-100"><Gecmis key={gecmisAnahtar} /></div>}
      </div>

      {duzenlenen && <DuzenleKutusu u={duzenlenen} onKapat={() => setDuzenlenen(null)} onOnizle={onizle} />}
      {kampanyaDuzenlenen && (
        <KampanyaDuzenleKutusu u={kampanyaDuzenlenen.u} k={kampanyaDuzenlenen.k}
          onKapat={() => setKampanyaDuzenlenen(null)} onOnizle={onizle} />
      )}
      {onizleme && (
        <OnizlemeKutusu onizleme={onizleme} yazmaAcik={veri.yazma.acik}
          onKapat={() => setOnizleme(null)}
          onGonderildi={() => { yukle(); setSecili(new Set()); setGecmisAnahtar(k => k + 1); }} />
      )}
    </div>
  );
}
